import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  LocalShipping as LocalShippingIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarTodayIcon,
  WaterDrop as WaterDropIcon,
  Euro as EuroIcon,
  Link as LinkIcon
} from '@mui/icons-material';

const ViewOrderModal = ({ 
  open, 
  onClose, 
  order,
  onEdit,
  onLinkDelivery 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  if (!order) return null;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/D';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'success';
      case 'confirmed': return 'info';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      case 'sent': return 'primary';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'delivered': return 'Consegnato';
      case 'confirmed': return 'Confermato';
      case 'pending': return 'In Attesa';
      case 'cancelled': return 'Annullato';
      case 'sent': return 'Inviato';
      default: return status;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'emergency': return 'error';
      case 'urgent': return 'warning';
      case 'normal': return 'default';
      default: return 'default';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'emergency': return 'Emergenza';
      case 'urgent': return 'Urgente';
      case 'normal': return 'Normale';
      default: return priority || 'Normale';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon color="primary" />
            <span>Ordine {order.order_code || order.id}</span>
          </Box>
          <Chip 
            label={getStatusLabel(order.status)}
            color={getStatusColor(order.status)}
            size="small"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {/* Dati Ordine */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Informazioni Ordine
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <CalendarTodayIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">Data Ordine</Typography>
                  </Box>
                  <Typography fontWeight="bold">{formatDate(order.order_date)}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <LocalShippingIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">Consegna Prevista</Typography>
                  </Box>
                  <Typography fontWeight="bold">{formatDate(order.expected_delivery_date)}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <WaterDropIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">Quantità</Typography>
                  </Box>
                  <Typography fontWeight="bold" color="primary">
                    {parseFloat(order.quantity_liters || 0).toLocaleString('it-IT')} L
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">Stato</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip 
                      label={getStatusLabel(order.status)}
                      color={getStatusColor(order.status)}
                    />
                  </Box>
                </Grid>
                {order.priority && order.priority !== 'normal' && (
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">Priorità</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip 
                        label={getPriorityLabel(order.priority)}
                        color={getPriorityColor(order.priority)}
                        size="small"
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
              {/* Informazioni aggiuntive: email e conferma */}
              {(order.email_sent || order.confirmed_date) && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {order.email_sent && (
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Email Inviata</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Chip label="Sì" color="success" size="small" variant="outlined" />
                        {order.email_sent_date && (
                          <Typography variant="body2" color="text.secondary">
                            il {formatDate(order.email_sent_date)}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  )}
                  {order.confirmed_date && (
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Data Conferma</Typography>
                      <Typography fontWeight="bold">{formatDate(order.confirmed_date)}</Typography>
                    </Grid>
                  )}
                </Grid>
              )}
            </Paper>
          </Grid>

          {/* Impianto */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Impianto / Contratto
              </Typography>
              {order.plant ? (
                <>
                  <Typography fontWeight="bold">
                    {order.plant.nome || order.plant.plant_code || `Impianto #${order.plant.id}`}
                  </Typography>
                  {order.plant.plant_code && order.plant.nome && (
                    <Typography variant="body2" color="text.secondary">
                      Codice: {order.plant.plant_code}
                    </Typography>
                  )}
                  {order.plant.delivery_code && (
                    <Typography variant="body2" color="text.secondary">
                      Cod. Consegna: {order.plant.delivery_code}
                    </Typography>
                  )}
                  {(order.plant.indirizzo || order.plant.citta) && (
                    <Typography variant="body2" color="text.secondary">
                      {[order.plant.indirizzo, order.plant.citta, order.plant.provincia].filter(Boolean).join(', ')}
                    </Typography>
                  )}
                  {order.plant.capacita_totale && (
                    <Typography variant="body2" color="text.secondary">
                      Capacità: {parseFloat(order.plant.capacita_totale).toLocaleString('it-IT')} L
                    </Typography>
                  )}
                  {order.contract && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">Contratto</Typography>
                      <Typography variant="body2">
                        {order.contract.cod_con || order.contract.nome || `#${order.contract.id}`}
                        {order.contract.customer && ` - ${order.contract.customer.ragione_sociale || order.contract.customer.nome}`}
                      </Typography>
                    </Box>
                  )}
                  {order.tank && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">Serbatoio</Typography>
                      <Typography variant="body2">
                        {order.tank.matricola || `Serbatoio #${order.tank.id}`} - {order.tank.capacita} L
                      </Typography>
                    </Box>
                  )}
                </>
              ) : order.contract ? (
                <>
                  <Typography fontWeight="bold">
                    {order.contract.cod_con || order.contract.nome || `Contratto #${order.contract.id}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {order.contract.nome || ''}
                  </Typography>
                  {order.contract.indirizzo && (
                    <Typography variant="body2" color="text.secondary">
                      {order.contract.indirizzo}
                    </Typography>
                  )}
                  {order.tank && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">Serbatoio</Typography>
                      <Typography variant="body2">
                        {order.tank.matricola || order.tank.codice || `Serbatoio #${order.tank.id}`} - {order.tank.capacita || order.tank.capacita_litri} L
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <Typography color="text.secondary">Nessun impianto associato</Typography>
              )}
            </Paper>
          </Grid>

          {/* Fornitore */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BusinessIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="primary">Fornitore</Typography>
              </Box>
              {order.supplier ? (
                <>
                  <Typography fontWeight="bold">
                    {order.supplier.company_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    P.IVA: {order.supplier.vat_number || 'N/D'}
                  </Typography>
                  {order.supplier.phone && (
                    <Typography variant="body2" color="text.secondary">
                      Tel: {order.supplier.phone}
                    </Typography>
                  )}
                  {order.supplier.email && (
                    <Typography variant="body2" color="text.secondary">
                      Email: {order.supplier.email}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography color="text.secondary">Nessun fornitore associato</Typography>
              )}
            </Paper>
          </Grid>

          {/* Prezzi */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EuroIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="primary">Dettaglio Economico</Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">Prezzo/Litro</Typography>
                  <Typography variant="h6">{formatCurrency(order.price_per_liter)}/L</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">Quantità</Typography>
                  <Typography variant="h6">
                    {parseFloat(order.quantity_liters || 0).toLocaleString('it-IT')} L
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">Totale Stimato</Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {formatCurrency(order.estimated_amount)}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">Consegna Collegata</Typography>
                  {order.delivery_id ? (
                    <Chip 
                      label={`Consegna #${order.delivery_id}`} 
                      color="success" 
                      icon={<LinkIcon />}
                      size="small"
                    />
                  ) : (
                    <Chip label="Non collegato" variant="outlined" size="small" />
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Consegna associata */}
          {order.delivery && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LocalShippingIcon color="success" fontSize="small" />
                  <Typography variant="subtitle2" color="success.dark">
                    Consegna Associata
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">DDT</Typography>
                    <Typography fontWeight="bold">{order.delivery.ddt_number || 'N/D'}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">Data Consegna</Typography>
                    <Typography>{formatDate(order.delivery.delivery_date)}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">Quantità Effettiva</Typography>
                    <Typography fontWeight="bold">
                      {parseFloat(order.delivery.quantity_liters || 0).toLocaleString('it-IT')} L
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">Totale</Typography>
                    <Typography fontWeight="bold" color="success.main">
                      {formatCurrency(order.delivery.total_amount)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Note */}
          {order.notes && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>Note</Typography>
                <Typography variant="body2">{order.notes}</Typography>
              </Paper>
            </Grid>
          )}

          {/* Metadati */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                Creato il: {formatDate(order.createdAt)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Ultimo aggiornamento: {formatDate(order.updatedAt)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {order.status !== 'cancelled' && order.status !== 'delivered' && !order.delivery_id && onLinkDelivery && (
          <Button 
            onClick={() => onLinkDelivery(order)} 
            startIcon={<LinkIcon />}
          >
            Collega a Consegna
          </Button>
        )}
        {onEdit && order.status !== 'cancelled' && (
          <Button onClick={() => onEdit(order)} color="primary">
            Modifica
          </Button>
        )}
        <Button onClick={onClose} variant="contained">Chiudi</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewOrderModal;
